import { useState } from 'react';
import clsx from 'clsx';

import { TooltipPro } from 'epn-ui';

import { faCalendarAlt } from '@fortawesome/pro-regular-svg-icons/faCalendarAlt';
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons/faChevronRight';
import { faCopy } from '@fortawesome/pro-regular-svg-icons/faCopy';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from 'epn-ui/umd/dist/components/Button';
import Dialog from 'epn-ui/umd/dist/components/Dialog';
import { toast } from 'react-toastify';

import useI18n from '@i18n';
import copyTextToClipboard from '@utils/clipboard';
import BaseToast from '@components/base/BaseToast';

import styles from './styles.module.scss';

export interface ICouponProps {
    url: string;
    logo: string;
    name: string;
    description?: string;
    promocode?: string;
    endDate: string;
    onStoreClickHandler?: () => void;
    onCodeCopyHandler?: () => void;
}

const Coupon: React.FC<ICouponProps> = ({
    url,
    logo,
    name,
    description,
    promocode,
    endDate,
    onStoreClickHandler,
    onCodeCopyHandler,
}) => {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const { t } = useI18n('Offers');

    const onCopyMessage = async value => {
        await copyTextToClipboard(value);
        onCodeCopyHandler();
        toast.success(<BaseToast text={t('Promocode copied')} type="success" />, {
            toastId: value,
        });
    };

    return (
        <article
            className={clsx(
                'flex flex-col justify-between rounded-[6px] shadow-1 p-[24px] h-full',
                styles.coupon,
            )}
        >
            <div className="h-8 mb-2 box-border">
                <picture>
                    <source type="image/webp" srcSet={`${logo}?f=webp`} />
                    <img
                        src={logo}
                        alt="Logo"
                        loading="lazy"
                        className="h-auto max-w-full max-h-full"
                    />
                </picture>
            </div>

            {promocode ? (
                <>
                    <p className="text-blueGray-600 text-[14px]">{t('Promocode')}</p>
                    <div className="flex flex-row items-center">
                        <TooltipPro title={promocode} placement="right">
                            <span
                                className="font-medium truncate text-[18px] text-blueGray-800"
                                data-tip={promocode}
                                data-for="promocode"
                            >
                                {promocode}
                            </span>
                        </TooltipPro>
                        <FontAwesomeIcon
                            icon={faCopy}
                            className="ml-2 cursor-pointer text-blueGray-300 text-[18px]"
                            onClick={() => onCopyMessage(promocode)}
                            fixedWidth
                        />
                    </div>
                </>
            ) : (
                <>
                    <TooltipPro title={name} placement="right">
                        <p
                            className={`font-medium text-blueGray-800 text-[18px] ${
                                description ? 'truncate' : 'line-clamp-2 '
                            }`}
                            data-tip={name}
                            data-for="name"
                        >
                            {name}
                        </p>
                    </TooltipPro>
                    <TooltipPro title={description} placement="right">
                        <div
                            className="line-clamp-2 text-blueGray-600 text-[16px]"
                            data-tip={description}
                            data-for="description"
                        >
                            {description}
                        </div>
                    </TooltipPro>
                </>
            )}

            <div className="flex flex-row items-start justify-between mt-4">
                <div className="flex flex-row">
                    <FontAwesomeIcon
                        icon={faCalendarAlt}
                        className="mt-1 mr-2 text-p1 text-blueGray-500"
                        fixedWidth
                    />
                    <div className="flex flex-col">
                        <span className="text-blueGray-500 text-[14px]">{t('End day')}</span>
                        <span className="font-medium text-blueGray-800 text-[14px]">{endDate}</span>
                    </div>
                </div>
                <button
                    type="button"
                    onClick={() => {
                        setIsOpen(true);
                    }}
                    className="w-12 h-12"
                >
                    <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 text-red-500 bg-red-100 rounded-full hover:bg-red-500 hover:text-white">
                        <FontAwesomeIcon className="text-p1" icon={faChevronRight} />
                    </div>
                </button>
            </div>
            <Dialog
                open={isOpen}
                onCancel={() => setIsOpen(false)}
                title={
                    <>
                        <img className="w-auto mb-2 h-9" src={logo} alt={`${name} logo`} />
                        <span className="font-medium text-p1 text-blueGray-800">{name}</span>
                    </>
                }
                size="sm"
            >
                <div id="coupon_dialog_modal">
                    <p className="mb-8 text-p1 text-blueGray-800">{description}</p>
                    <Button
                        type="primary"
                        onClick={onStoreClickHandler}
                        target="_blank"
                        rel="nofollow noopener"
                        href={url}
                    >
                        {t('Go to store')}
                    </Button>
                </div>
            </Dialog>
        </article>
    );
};

export default Coupon;
